import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  DraftSabotageConfig, 
  DraftSabotageSettings, 
  defaultDraftSabotageSettings,
  mlbDraftSabotageSettings,
} from '@/types/draftSabotageSettings';
import { useSport } from '@/contexts/SportContext';

interface DraftSabotageContextType {
  settings: DraftSabotageSettings;
  activeSabotages: DraftSabotageConfig[];
  updateSettings: (settings: DraftSabotageSettings) => void;
  updateSabotage: (sabotage: DraftSabotageConfig) => void;
  addSabotage: (sabotage: DraftSabotageConfig) => void;
  deleteSabotage: (id: string) => void;
  toggleSabotageStatus: (id: string) => boolean; // returns success
  updateMaxPerDraft: (value: number) => void;
  recordSabotageUsage: (id: string, cost: number) => void;
}

const DraftSabotageContext = createContext<DraftSabotageContextType | undefined>(undefined);

export const DraftSabotageProvider = ({ children }: { children: ReactNode }) => {
  const { activeSport } = useSport();
  const [settings, setSettings] = useState<DraftSabotageSettings>(defaultDraftSabotageSettings);

  // Switch sabotage settings when sport changes
  useEffect(() => {
    setSettings(activeSport === 'mlb' ? mlbDraftSabotageSettings : defaultDraftSabotageSettings);
  }, [activeSport]);

  // Get only active sabotages
  const activeSabotages = settings.sabotages.filter(s => s.isActive);

  const updateSettings = (newSettings: DraftSabotageSettings) => {
    setSettings(newSettings);
  };

  const updateSabotage = (updated: DraftSabotageConfig) => {
    setSettings(prev => ({
      ...prev,
      sabotages: prev.sabotages.map(s => s.id === updated.id ? updated : s),
    }));
  };

  const addSabotage = (sabotage: DraftSabotageConfig) => {
    setSettings(prev => ({
      ...prev,
      sabotages: [...prev.sabotages, sabotage],
    }));
  };

  const deleteSabotage = (id: string) => {
    setSettings(prev => ({
      ...prev,
      sabotages: prev.sabotages.filter(s => s.id !== id),
    }));
  };

  const toggleSabotageStatus = (id: string): boolean => {
    const sabotage = settings.sabotages.find(s => s.id === id);
    if (!sabotage) return false;

    const currentActive = settings.sabotages.filter(s => s.isActive);
    const currentInactive = settings.sabotages.filter(s => !s.isActive);

    if (sabotage.isActive) {
      // Moving to inactive - always allowed
      setSettings(prev => ({
        ...prev,
        sabotages: prev.sabotages.map(s => 
          s.id === id ? { ...s, isActive: false } : s
        ),
      }));
      return true;
    } else {
      // Moving to active - check limit
      if (currentActive.length >= settings.maxActive) {
        return false; // At max active limit
      }
      setSettings(prev => ({
        ...prev,
        sabotages: prev.sabotages.map(s => 
          s.id === id ? { ...s, isActive: true } : s
        ),
      }));
      return true;
    }
  };

  const updateMaxPerDraft = (value: number) => {
    setSettings(prev => ({ ...prev, maxPerDraft: value }));
  };

  const recordSabotageUsage = (id: string, cost: number) => {
    setSettings(prev => ({
      ...prev,
      sabotages: prev.sabotages.map(s => 
        s.id === id 
          ? { ...s, timesUsed: s.timesUsed + 1, totalCostCollected: s.totalCostCollected + cost }
          : s
      ),
    }));
  };

  return (
    <DraftSabotageContext.Provider value={{
      settings,
      activeSabotages,
      updateSettings,
      updateSabotage,
      addSabotage,
      deleteSabotage,
      toggleSabotageStatus,
      updateMaxPerDraft,
      recordSabotageUsage,
    }}>
      {children}
    </DraftSabotageContext.Provider>
  );
};

export const useDraftSabotage = () => {
  const context = useContext(DraftSabotageContext);
  if (context === undefined) {
    throw new Error('useDraftSabotage must be used within a DraftSabotageProvider');
  }
  return context;
};
